import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { UserUsage } from '../types/limits';
import { getUserUsage } from '../services/userRecordsService';

interface UseUserRecordsReturn {
  usage: UserUsage | null;
  isLoading: boolean;
  error: string | null;
  refreshUsage: () => Promise<void>;
}

export const useUserRecords = (): UseUserRecordsReturn => {
  const { user } = useAuth();
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUsage = async () => {
    if (!user?.uid) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const currentUsage = await getUserUsage(user.uid);
      setUsage(currentUsage);
    } catch (err) {
      setError('Erro ao carregar dados de uso');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.uid) {
      setUsage(null);
      setIsLoading(false);
      return;
    }

    const usageRef = doc(db, 'users', user.uid, 'usage', 'current');
    
    const unsubscribe = onSnapshot(
      usageRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setUsage({
            ...data,
            lastResetDate: data.lastResetDate?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          } as UserUsage);
        } else {
          refreshUsage();
        }
        setIsLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Error fetching usage:', error);
        setError(error.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  return {
    usage,
    isLoading,
    error,
    refreshUsage,
  };
};
